import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Factory,
  Heart, 
  Palette, 
  Wrench,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import HeroImage from '../components/HeroImage';
import AnimatedSection from '../components/AnimatedSection';

function Services() {
  const GOOGLE_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLScVJ5NP38k3xJuhVYWnZSFm8PKrzA6Dqu7U73X9fdCI5A2PtQ/viewform?usp=header";
  
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen mx-[4.2mm] mt-[4.2mm]">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#00ffff]/10 via-transparent to-[#ff0000]/10 animate-gradient rounded-lg z-10"></div>
          <div className="absolute inset-0 rounded-lg overflow-hidden">
            <HeroImage 
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&q=80&w=1920&h=1080" 
              alt="Business meeting in office"
              priority={true}
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50 rounded-lg z-20"></div>
        </div>

        {/* Hero Content */}
        <div className="relative h-full flex items-center z-30">
          <div className="max-w-7xl mx-auto px-4">
            <h1 className="text-5xl font-bold text-center text-white mb-6">The Atlas Advantage</h1>
            <p className="text-xl text-white/90 text-center max-w-3xl mx-auto">
              We bring a level of creativity, know-how and combined expertise to your table that our competitors could not match - <strong>if we had any</strong>. Take a look at the services we offer and see for yourself!
            </p>
          </div>
        </div>
      </section>

      {/* Detailed Services */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="space-y-24">
          {/* Lead Generation */}
          <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
            <AnimatedSection className="w-full md:w-1/2">
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800"
                alt="Lead Generation"
                className="rounded-xl shadow-lg w-full h-[300px] md:h-[400px] object-cover"
              />
            </AnimatedSection>
            <AnimatedSection className="w-full md:w-1/2 space-y-6 text-center">
              <div className="flex justify-center mb-6">
                <Factory className="h-12 w-12 text-[#00ffff]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Lead Generation</h2>
              <p className="text-white/80 text-base md:text-lg mb-6 md:mb-8">Atlas does the leg work - finding clients in every corner of the Internet, and beyond.</p>
              <ul className="space-y-4">
                {[
                  "AI-driven prospect identification and scoring",
                  "Automated multi-channel outreach campaigns",
                  "Real-time lead qualification and routing",
                  "Advanced analytics and conversion tracking"
                ].map((feature, index) => (
                  <li key={index} className="flex items-center justify-center text-white/80">
                    <CheckCircle className="h-5 w-5 text-[#00ffff] mr-3 flex-shrink-0" />
                    <span className="text-sm md:text-base">{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="flex justify-center">
                <Link 
                  to="/services/lead-generation"
                  className="mt-8 px-6 py-3 border-2 border-[#00ffff] text-[#00ffff] rounded-lg hover:bg-[#ff0000] hover:border-[#ff0000] hover:text-white transition-colors font-semibold inline-flex items-center"
                >
                  Show Me
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </AnimatedSection>
          </div>

          {/* Customer Retention */}
          <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
            <AnimatedSection className="w-full md:w-1/2">
              <img
                src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&q=80&w=800"
                alt="Customer Retention"
                className="rounded-xl shadow-lg w-full h-[300px] md:h-[400px] object-cover"
              />
            </AnimatedSection>
            <AnimatedSection className="w-full md:w-1/2 space-y-6 text-center">
              <div className="flex justify-center mb-6">
                <Heart className="h-12 w-12 text-[#ff0000]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Customer Retention Solutions</h2>
              <p className="text-white/80 text-base md:text-lg mb-6 md:mb-8">We enable and help you develop strategies that keep your customers coming back.</p>
              <ul className="space-y-4">
                {[
                  "Predictive churn analysis and prevention",
                  "Personalized customer engagement programs",
                  "Customer satisfaction monitoring",
                  "Loyalty program optimization"
                ].map((feature, index) => (
                  <li key={index} className="flex items-center justify-center text-white/80">
                    <CheckCircle className="h-5 w-5 text-[#00ffff] mr-3 flex-shrink-0" />
                    <span className="text-sm md:text-base">{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="flex justify-center">
                <Link 
                  to="/services/customer-retention"
                  className="mt-8 px-6 py-3 border-2 border-[#00ffff] text-[#00ffff] rounded-lg hover:bg-[#ff0000] hover:border-[#ff0000] hover:text-white transition-colors font-semibold inline-flex items-center"
                >
                  Show Me
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </AnimatedSection>
          </div>

          {/* Website Creation */}
          <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
            <AnimatedSection className="w-full md:w-1/2">
              <img
                src="https://images.unsplash.com/photo-1467232004584-a241de8bcf5d?auto=format&fit=crop&q=80&w=800"
                alt="Website Creation"
                className="rounded-xl shadow-lg w-full h-[300px] md:h-[400px] object-cover"
              />
            </AnimatedSection>
            <AnimatedSection className="w-full md:w-1/2 space-y-6 text-center">
              <div className="flex justify-center mb-6">
                <Palette className="h-12 w-12 text-[#00ffff]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Website Creation & Curation</h2>
              <p className="text-white/80 text-base md:text-lg mb-6 md:mb-8">Your website is one of your biggest statements, if not the first. So let Atlas build it for you!</p>
              <ul className="space-y-4">
                {[
                  "AI-powered design optimization",
                  "Conversion-focused user experience",
                  "Mobile-first responsive design",
                  "Automated content management"
                ].map((feature, index) => (
                  <li key={index} className="flex items-center justify-center text-white/80">
                    <CheckCircle className="h-5 w-5 text-[#00ffff] mr-3 flex-shrink-0" />
                    <span className="text-sm md:text-base">{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="flex justify-center">
                <Link 
                  to="/services/website-creation"
                  className="mt-8 px-6 py-3 border-2 border-[#00ffff] text-[#00ffff] rounded-lg hover:bg-[#ff0000] hover:border-[#ff0000] hover:text-white transition-colors font-semibold inline-flex items-center"
                >
                  Show Me
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </AnimatedSection>
          </div>

          {/* Custom Solutions */}
          <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
            <AnimatedSection className="w-full md:w-1/2">
              <img
                src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&q=80&w=800"
                alt="Custom Solutions"
                className="rounded-xl shadow-lg w-full h-[300px] md:h-[400px] object-cover"
              />
            </AnimatedSection>
            <AnimatedSection className="w-full md:w-1/2 space-y-6 text-center">
              <div className="flex justify-center mb-6">
                <Wrench className="h-12 w-12 text-[#ff0000]" />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Custom Solutions</h2>
              <p className="text-white/80 text-base md:text-lg mb-6 md:mb-8">Combine our capabilities to solve your newest and most complex challenges.</p>
              <ul className="space-y-4">
                {[
                  "Custom AI model development",
                  "Business process automation",
                  "Integration with existing systems",
                  "Scalable infrastructure design"
                ].map((feature, index) => (
                  <li key={index} className="flex items-center justify-center text-white/80">
                    <CheckCircle className="h-5 w-5 text-[#00ffff] mr-3 flex-shrink-0" />
                    <span className="text-sm md:text-base">{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="flex justify-center">
                <Link 
                  to="/services/custom-solutions"
                  className="mt-8 px-6 py-3 border-2 border-[#00ffff] text-[#00ffff] rounded-lg hover:bg-[#ff0000] hover:border-[#ff0000] hover:text-white transition-colors font-semibold inline-flex items-center"
                >
                  Show Me
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <AnimatedSection className="max-w-7xl mx-auto px-4 py-24">
        <div className="white-holographic-card p-12 rounded-2xl text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-6">Ready to Transform Your Business?</h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Let's discuss how our AI-powered solutions can help you achieve your business goals.
          </p>
          <a
            href={GOOGLE_FORM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 bg-[#00ffff] text-white rounded-lg hover:bg-[#ff0000] transition-colors font-semibold inline-flex items-center mx-auto"
          >
            Get Started
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
      </AnimatedSection>
    </div>
  );
}

export default Services;